import dayjs from 'dayjs';
import type { FormattedVenueOpeningHour } from '@/entities/venue/lib/get-formatted-venue-opening-hours.util';
import { capitalize } from '@/shared/lib/capitalize';

export type GroupedVenueOpeningHourRow = {
    dayRange: string;
    intervals: Array<{ start: string; end: string }>;
};

/**
 * Groups formatted opening hours into consecutive day ranges that share the same intervals
 * (e.g. "Mon - Fri 10:00 - 22:00").
 */
export const groupFormattedVenueOpeningHours = (
    openingHours: FormattedVenueOpeningHour[],
    locale: string,
): GroupedVenueOpeningHourRow[] => {
    const getShortDayName = (dayOfWeek: number): string =>
        capitalize(dayjs().day(dayOfWeek).locale(locale.toLowerCase()).format('ddd'));

    const intervalGroups = new Map<
        string,
        Array<{ day: string; dayOfWeek: number; intervals: Array<{ start: string; end: string }> }>
    >();

    openingHours.forEach(({ dayOfWeek, intervals }) => {
        const intervalsKey =
            intervals.length === 0 ? 'closed' : intervals.map((i) => `${i.start}-${i.end}`).join(',');
        const shortDay = getShortDayName(dayOfWeek);
        if (!intervalGroups.has(intervalsKey)) {
            intervalGroups.set(intervalsKey, []);
        }
        intervalGroups.get(intervalsKey)!.push({ day: shortDay, dayOfWeek, intervals });
    });

    const result: GroupedVenueOpeningHourRow[] = [];

    intervalGroups.forEach((days) => {
        days.sort((a, b) => {
            const dayA = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
            const dayB = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
            return dayA - dayB;
        });

        const sequences: Array<
            Array<{ day: string; dayOfWeek: number; intervals: Array<{ start: string; end: string }> }>
        > = [];
        let currentSequence = [days[0]];

        for (let i = 1; i < days.length; i++) {
            const prevDayOfWeek = days[i - 1].dayOfWeek === 0 ? 7 : days[i - 1].dayOfWeek;
            const currDayOfWeek = days[i].dayOfWeek === 0 ? 7 : days[i].dayOfWeek;
            const isConsecutive =
                currDayOfWeek === prevDayOfWeek + 1 || (prevDayOfWeek === 7 && currDayOfWeek === 1);
            if (isConsecutive) {
                currentSequence.push(days[i]);
            } else {
                sequences.push(currentSequence);
                currentSequence = [days[i]];
            }
        }
        sequences.push(currentSequence);

        sequences.forEach((sequence) => {
            const intervals = sequence[0].intervals;
            const dayRange =
                sequence.length === 1
                    ? sequence[0].day
                    : `${sequence[0].day} - ${sequence[sequence.length - 1].day}`;
            result.push({ dayRange, intervals });
        });
    });

    const dayNameToDayOfWeek = new Map<string, number>();
    openingHours.forEach(({ dayOfWeek }) => {
        const shortDay = getShortDayName(dayOfWeek);
        if (!dayNameToDayOfWeek.has(shortDay)) {
            dayNameToDayOfWeek.set(shortDay, dayOfWeek);
        }
    });

    result.sort((a, b) => {
        const getDayOfWeekSortValue = (dayRange: string) => {
            const dayOfWeek = dayNameToDayOfWeek.get(dayRange.split(' - ')[0]) ?? 0;
            return dayOfWeek === 0 ? 7 : dayOfWeek;
        };
        return getDayOfWeekSortValue(a.dayRange) - getDayOfWeekSortValue(b.dayRange);
    });

    return result;
};
